import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BarChart3, Eye, MousePointerClick, Plus, Users, FileText, Share2, Radar, PenSquare, Inbox } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Skeleton } from "../../components/ui/skeleton";
import AdminPageHeader from "../../components/admin/AdminPageHeader";
import PagesTable from "../../components/admin/PagesTable";
import { ADMIN_AREAS } from "../../components/admin/AdminHeader";
import { errorMessage, pagesApi } from "../../lib/api";

const RECENT_LIMIT = 5;

const Kpi = ({ icon: Icon, label, value, hint, testId, to }) => {
  const inner = (
    <>
      <div className="flex items-center justify-between">
        <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-muted-foreground">{label}</p>
        <Icon className="h-4 w-4 text-primary" />
      </div>
      <p className="display mt-3 text-5xl leading-none">{value}</p>
      {hint && <p className="mt-2 text-xs text-muted-foreground">{hint}</p>}
    </>
  );
  if (to) {
    return (
      <Link to={to} className="block border border-border bg-card p-5 transition-colors hover:border-primary/60" data-testid={testId}>
        {inner}
      </Link>
    );
  }
  return (
    <div className="border border-border bg-card p-5" data-testid={testId}>
      {inner}
    </div>
  );
};

const AreaCard = ({ area }) => (
  <Link
    to={area.to}
    className="group flex flex-col justify-between border border-border bg-card p-5 transition-colors hover:border-primary/60 hover:bg-card/80"
    data-testid={`area-card-${area.id}`}
  >
    <div>
      <span className="flex h-10 w-10 items-center justify-center border border-border text-primary transition-colors group-hover:border-primary group-hover:bg-primary group-hover:text-primary-foreground">
        <area.icon className="h-5 w-5" />
      </span>
      <h3 className="display mt-4 text-2xl uppercase tracking-wide">{area.label}</h3>
      <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{area.blurb}</p>
    </div>
    <span className="mt-4 inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-primary">
      Open <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
    </span>
  </Link>
);

const STEPS = [
  { icon: PenSquare, title: "Build a page", body: "Write the offer, add sections and pick a hero photo in the editor.", to: "/admin/pages/new", cta: "New page" },
  { icon: Radar, title: "Add your pixels", body: "Turn on Meta, Google or TikTok in the Pixel & Tracking tab so ad platforms see conversions.", to: "/admin/pages", cta: "Open pages" },
  { icon: Share2, title: "Share the link", body: "Copy the campaign link (with UTM tags) into your ads, bio or stories.", to: "/admin/pages", cta: "Copy links" },
  { icon: BarChart3, title: "Watch the stats", body: "Views, clicks and the weekly report show whether a promo is gaining or fading.", to: "/admin/pages", cta: "See stats" },
];

const AdminDashboard = () => {
  const [overview, setOverview] = useState(null);
  const [pages, setPages] = useState(null);

  const load = useCallback(async () => {
    try {
      const [o, p] = await Promise.all([pagesApi.overview(), pagesApi.list()]);
      setOverview(o);
      setPages(p);
    } catch (err) {
      toast.error(errorMessage(err, "Could not load dashboard"));
      setPages([]);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const recent = pages ? pages.slice(0, RECENT_LIMIT) : null;
  const areas = ADMIN_AREAS.filter((a) => a.id !== "dashboard");

  return (
    <div className="space-y-10" data-testid="admin-dashboard">
      <AdminPageHeader
        eyebrow="Overview"
        title="Dashboard"
        description="Everything for your promotions in one place. Use the links below (or the header) to jump to any area."
        actions={
          <Button asChild className="h-11 rounded-none px-6 text-[11px] font-semibold uppercase tracking-[0.2em]" data-testid="dashboard-new-page">
            <Link to="/admin/pages/new">
              <Plus className="mr-2 h-4 w-4" /> New landing page
            </Link>
          </Button>
        }
      />

      {/* KPIs */}
      <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-6" aria-label="Totals">
        {overview ? (
          <>
            <Kpi icon={Inbox} label="New submissions" value={overview.submissions_new} hint={`${overview.submissions_total} total · open inbox`} testId="kpi-submissions" to="/admin/submissions" />
            <Kpi icon={FileText} label="Pages" value={overview.pages} hint={`${overview.published} published`} testId="kpi-pages" />
            <Kpi icon={Eye} label="Total views" value={overview.views} testId="kpi-views" />
            <Kpi icon={Users} label="Unique visitors" value={overview.unique_visitors} testId="kpi-uniques" />
            <Kpi icon={MousePointerClick} label="CTA clicks" value={overview.clicks} testId="kpi-clicks" />
            <Kpi icon={BarChart3} label="Click-through" value={`${overview.ctr}%`} hint="clicks / views" testId="kpi-ctr" />
          </>
        ) : (
          ["a", "b", "c", "d", "e", "f"].map((k) => <Skeleton key={k} className="h-28" />)
        )}
      </section>

      {/* Areas */}
      <section data-testid="dashboard-areas">
        <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-muted-foreground">Go to</p>
        <div className="mt-3 grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {areas.map((area) => (
            <AreaCard key={area.id} area={area} />
          ))}
        </div>
      </section>

      {/* Recent pages */}
      <section data-testid="dashboard-recent">
        <div className="mb-3 flex items-end justify-between gap-4">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-muted-foreground">Recent landing pages</p>
            {pages && pages.length > RECENT_LIMIT && <p className="mt-1 text-xs text-muted-foreground">Showing {RECENT_LIMIT} of {pages.length}</p>}
          </div>
          <Link to="/admin/pages" className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-primary hover:text-foreground" data-testid="dashboard-all-pages">
            All landing pages <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
        <PagesTable pages={recent} onChanged={load} />
      </section>

      {/* How it works */}
      <section className="border border-border bg-card p-6" data-testid="dashboard-how-it-works">
        <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-muted-foreground">How it works</p>
        <div className="mt-4 grid gap-px bg-border/60 sm:grid-cols-2 xl:grid-cols-4">
          {STEPS.map((s, i) => (
            <div key={s.title} className="flex flex-col bg-card p-5">
              <div className="flex items-center gap-3">
                <span className="display text-3xl text-primary">{String(i + 1).padStart(2, "0")}</span>
                <s.icon className="h-4 w-4 text-muted-foreground" />
              </div>
              <h3 className="mt-3 text-sm font-semibold">{s.title}</h3>
              <p className="mt-1 flex-1 text-sm leading-relaxed text-muted-foreground">{s.body}</p>
              <Link to={s.to} className="mt-4 inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.2em] text-primary hover:text-foreground">
                {s.cta} <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default AdminDashboard;
